import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDesignerStore } from '../store';
import { generateExcel } from '@calculom/report-designer';
import {
  MOCK_COMPANIES,
  MOCK_PERIODS,
  MOCK_ACCOUNTS,
  MOCK_CONNECTIONS,
  MOCK_DATE_FISCAL,
} from '../mock-data';
import type { ReportType } from '@calculom/report-designer';
import { trpc } from '../../../../lib/trpc';

const REPORT_TYPES: { value: ReportType; label: string }[] = [
  { value: 'income_statement', label: 'Income Statement' },
  { value: 'balance_sheet', label: 'Balance Sheet' },
  { value: 'trial_balance', label: 'Trial Balance' },
  { value: 'cash_flow', label: 'Cash Flow' },
  { value: 'budget_vs_actual', label: 'Budget vs Actual' },
  { value: 'custom', label: 'Custom' },
];

export function DesignerToolbar() {
  const navigate = useNavigate();
  const {
    definition,
    nodes,
    columnConfig,
    layoutSettings,
    isDirty,
    templateId,
    instructionText,
    setName,
    setType,
    setTemplateId,
    reset,
    setColumnModalOpen,
  } = useDesignerStore();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const utils = trpc.useUtils();
  const createTemplate = trpc.fra.createTemplate.useMutation();
  const updateTemplate = trpc.fra.updateTemplate.useMutation();

  /** Check whether a template with the given name already exists (excluding current template). */
  const checkDuplicateName = async (name: string, excludeId?: string | null): Promise<boolean> => {
    try {
      const existing = await utils.fra.listTemplates.fetch({ limit: 200 });
      return existing.some(
        (t) => t.name.toLowerCase() === name.toLowerCase() && t.id !== excludeId,
      );
    } catch {
      // If we can't check, allow the save to proceed
      return false;
    }
  };

  const handleDownloadExcel = async () => {
    setIsGenerating(true);
    try {
      const dataContext = {
        companies: MOCK_COMPANIES,
        periods: MOCK_PERIODS,
        accounts: MOCK_ACCOUNTS,
        erpConnections: MOCK_CONNECTIONS,
        dateFiscalInfo: MOCK_DATE_FISCAL,
      };
      await generateExcel(definition, dataContext, nodes, columnConfig, layoutSettings, instructionText ?? undefined);
    } catch (err) {
      console.error('Excel generation failed:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const templateData = {
        ...definition,
        nodes,
        columnConfig,
        layoutSettings,
      };

      // For new saves, check for duplicate names
      if (!templateId) {
        const isDuplicate = await checkDuplicateName(definition.name);
        if (isDuplicate) {
          setSaveMessage('A report with this name already exists. Please use a different name.');
          setTimeout(() => setSaveMessage(null), 4000);
          setIsSaving(false);
          return;
        }
      }

      // For updates, check if another template (not this one) has the same name
      if (templateId) {
        const isDuplicate = await checkDuplicateName(definition.name, templateId);
        if (isDuplicate) {
          setSaveMessage('Another report with this name already exists. Please use a different name.');
          setTimeout(() => setSaveMessage(null), 4000);
          setIsSaving(false);
          return;
        }
        await updateTemplate.mutateAsync({
          id: templateId,
          name: definition.name,
          category: definition.type,
          templateData,
        });
      } else {
        const result = await createTemplate.mutateAsync({
          name: definition.name,
          category: definition.type,
          templateData,
          isPublic: false,
        });
        setTemplateId(result.id);
      }

      // Mark as clean after successful save
      useDesignerStore.setState({ isDirty: false });
      setSaveMessage('Saved');
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (err) {
      console.error('Save failed:', err);
      setSaveMessage('Save failed');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = async () => {
    await utils.fra.listTemplates.invalidate();
    navigate('/reports');
  };

  const handleSaveAsNew = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      // Require the user to rename the report before saving as new
      const isDuplicate = await checkDuplicateName(definition.name);
      if (isDuplicate) {
        setSaveMessage('Please rename the report before saving as new.');
        setTimeout(() => setSaveMessage(null), 4000);
        setIsSaving(false);
        return;
      }

      const templateData = {
        ...definition,
        nodes,
        columnConfig,
        layoutSettings,
      };

      const result = await createTemplate.mutateAsync({
        name: definition.name,
        category: definition.type,
        templateData,
        isPublic: false,
      });
      setTemplateId(result.id);

      useDesignerStore.setState({ isDirty: false });
      setSaveMessage('Saved as new report');
      setTimeout(() => setSaveMessage(null), 2000);
    } catch (err) {
      console.error('Save As New failed:', err);
      setSaveMessage('Save failed');
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const hasContent = nodes.length > 0 || definition.rows.length > 0;
  const isDefaultName = definition.name === 'Untitled Report';

  return (
    <div className="flex items-center gap-4 px-4 py-2 bg-white border-b border-gray-200">
      <button
        onClick={handleBack}
        className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1"
        title="Back to Reports"
      >
        &larr; Reports
      </button>

      <div className="w-px h-6 bg-gray-200" />

      <input
        type="text"
        value={definition.name}
        onChange={(e) => setName(e.target.value)}
        className={`text-lg font-semibold border-b hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1 py-0.5 ${
          isDefaultName
            ? 'text-blue-600 border-blue-300'
            : 'text-gray-900 border-transparent'
        }`}
      />

      <select
        value={definition.type}
        onChange={(e) => setType(e.target.value as ReportType)}
        className="text-sm border border-gray-300 rounded px-2 py-1"
      >
        {REPORT_TYPES.map((rt) => (
          <option key={rt.value} value={rt.value}>{rt.label}</option>
        ))}
      </select>

      {isDirty && !saveMessage && (
        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
          Unsaved changes
        </span>
      )}

      {saveMessage && (
        <span className={`text-xs px-2 py-0.5 rounded whitespace-nowrap ${
          saveMessage === 'Saved' || saveMessage === 'Saved as new report'
            ? 'text-green-600 bg-green-50'
            : saveMessage.includes('already exists')
              ? 'text-amber-700 bg-amber-50'
              : 'text-red-600 bg-red-50'
        }`}>
          {saveMessage}
        </span>
      )}

      <div className="flex-1" />

      <button
        onClick={() => setColumnModalOpen(true)}
        className="text-sm text-blue-700 hover:text-blue-900 px-3 py-1 border border-blue-300 rounded hover:bg-blue-50"
      >
        Column Definitions
      </button>

      <button
        onClick={handleSave}
        disabled={isSaving || !hasContent}
        className="text-sm text-indigo-700 hover:text-indigo-900 px-3 py-1 border border-indigo-300 rounded hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? 'Saving...' : templateId ? 'Update Report' : 'Save New'}
      </button>

      {templateId && (
        <button
          onClick={handleSaveAsNew}
          disabled={isSaving || !hasContent}
          className="text-sm text-indigo-500 hover:text-indigo-700 px-3 py-1 border border-indigo-200 rounded hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save As New
        </button>
      )}

      <button
        onClick={handleDownloadExcel}
        disabled={isGenerating || !hasContent}
        className="text-sm text-green-700 hover:text-green-900 px-3 py-1 border border-green-300 rounded hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isGenerating ? 'Generating...' : 'Download Excel'}
      </button>

      <button
        onClick={reset}
        className="text-sm text-red-600 hover:text-red-800 px-3 py-1 border border-red-200 rounded hover:bg-red-50"
      >
        Reset
      </button>
    </div>
  );
}
